import './Search.css';
import CreateClient from '../Upload/Upload.js';
import '../../Dashboard.css';
import SERVER_URL from '../../../../env.js';
import { useEffect, useState, useCallback } from 'react';
import Cookies from 'js-cookie';

const TITLE_OPTIONS = ['', 'Mr.', 'Mrs.', 'Ms.', 'Dr.', 'Prof.', 'Other'];
const MARITAL_OPTIONS = ['', 'Single', 'Married', 'Common-Law', 'Divorced', 'Widowed'];

const ADDITIONAL_FIELD_KEYS = [
    'sin', 'date_of_birth', 'address', 'title',
    'citizenship_status', 'marital_status', 'spouse_name', 'language',
];

const ADDITIONAL_FIELD_LABELS = {
    sin: 'SIN',
    date_of_birth: 'Date of Birth',
    address: 'Address',
    title: 'Title',
    citizenship_status: 'Canadian Citizen',
    marital_status: 'Marital Status',
    spouse_name: 'Spouse Name',
    language: 'Language',
};

/** Returns true if a client has at least one additional field with a meaningful value */
function hasAdditionalFields(client) {
    return ADDITIONAL_FIELD_KEYS.some((key) => {
        const val = client[key];
        if (key === 'citizenship_status') return val === true;
        if (key === 'language') return val && val !== 'english'; // only show if non-default
        return val !== null && val !== undefined && val !== '';
    });
}

const EMPTY_EDIT = {
    client_name: '',
    emails: '',
    phone: '',
    auth_rep: false,
    my_cra: false,
    notes: '',
    do_not_contact: false,
    do_not_contact_reason: '',
    // Additional fields
    sin: '',
    date_of_birth: '',
    address: '',
    title: '',
    citizenship_status: false,
    marital_status: '',
    spouse_name: '',
    language: 'english',
};

function Search({ toggleNav, currentClientID, setCurrentClientID, setCurrentClientName }) {
    const [clients, setClients] = useState([]);
    const [searchName, setSearchName] = useState('');
    const [loading, setLoading] = useState(true);
    const [updateMessage, setUpdateMessage] = useState('');

    // Edit modal state
    const [editingClient, setEditingClient] = useState(null);
    const [editFields, setEditFields] = useState(EMPTY_EDIT);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    const [deleting, setDeleting] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);

    // Add modal state
    const [addModalOpen, setAddModalOpen] = useState(false);
    const openAddModal = () => setAddModalOpen(true);
    const closeAddModal = () => setAddModalOpen(false);

    // Per-card additional fields expansion (Set of client IDs)
    const [expandedCards, setExpandedCards] = useState(new Set());

    const toggleExpandCard = (clientId) => {
        setExpandedCards((prev) => {
            const next = new Set(prev);
            if (next.has(clientId)) {
                next.delete(clientId);
            } else {
                next.add(clientId);
            }
            return next;
        });
    };

    const searchFilter = useCallback((client) => {
        if (!searchName) return true;
        return client?.client_name?.toLowerCase().includes(searchName.toLowerCase());
    }, [searchName]);

    const fetchClients = useCallback(() => {
        setLoading(true);
        fetch(SERVER_URL + '/clients', {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + Cookies.get('accessToken'),
            },
        })
            .then(async (response) => {
                if (!response.ok) {
                    const errData = await response.json().catch(() => ({}));
                    throw new Error(errData.error || `HTTP error ${response.status}`);
                }
                return response.json();
            })
            .then((data) => {
                setClients(data.clients || []);
            })
            .catch((error) => {
                setUpdateMessage(`Error loading clients: ${error.message}`);
            })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { fetchClients(); }, [fetchClients]);

    const openEdit = (client) => {
        setEditingClient(client);
        setEditFields({
            client_name: client.client_name || '',
            emails: client.emails || '',
            phone: client.phone || '',
            auth_rep: client.auth_rep || false,
            my_cra: client.my_cra || false,
            notes: client.notes || '',
            do_not_contact: client.do_not_contact || false,
            do_not_contact_reason: client.do_not_contact_reason || '',
            // Additional fields
            sin: client.sin || '',
            date_of_birth: client.date_of_birth || '',
            address: client.address || '',
            title: client.title || '',
            citizenship_status: client.citizenship_status || false,
            marital_status: client.marital_status || '',
            spouse_name: client.spouse_name || '',
            language: client.language || 'english',
        });
        setSaveError('');
        setConfirmDelete(false);
    };

    const closeEdit = () => {
        setEditingClient(null);
        setSaveError('');
        setConfirmDelete(false);
    };

    const handleFieldChange = (e) => {
        const { name, value, type, checked } = e.target;
        setEditFields((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const saveEdit = async () => {
        setSaving(true);
        setSaveError('');
        try {
            const response = await fetch(`${SERVER_URL}/clients/${editingClient.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + Cookies.get('accessToken'),
                },
                body: JSON.stringify(editFields),
            });
            const data = await response.json();
            if (!response.ok) {
                setSaveError(data.error || 'An error occurred while saving.');
            } else {
                setClients((prev) =>
                    prev.map((c) => (c.id === editingClient.id ? { ...c, ...data.client } : c))
                );
                closeEdit();
            }
        } catch (err) {
            setSaveError('An unexpected error occurred.');
        } finally {
            setSaving(false);
        }
    };

    const deleteClient = async () => {
        if (!confirmDelete) {
            setConfirmDelete(true);
            return;
        }
        setDeleting(true);
        try {
            const response = await fetch(`${SERVER_URL}/clients/${editingClient.id}`, {
                method: 'DELETE',
                headers: { 'Authorization': 'Bearer ' + Cookies.get('accessToken') },
            });
            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                setSaveError(data.error || 'Failed to delete client.');
            } else {
                if (currentClientID === editingClient.id) {
                    setCurrentClientID(null);
                    setCurrentClientName(null);
                }
                setClients((prev) => prev.filter((c) => c.id !== editingClient.id));
                closeEdit();
            }
        } catch (err) {
            setSaveError('An unexpected error occurred.');
        } finally {
            setDeleting(false);
        }
    };

    const filtered = clients.filter(searchFilter);

    // Helper to render a single additional field value
    const renderAdditionalValue = (client, key) => {
        const val = client[key];
        if (key === 'citizenship_status') return val ? 'Yes' : null;
        if (key === 'language') return val ? val.charAt(0).toUpperCase() + val.slice(1) : null;
        return val || null;
    };

    const editShowSpouseName = editFields.marital_status === 'Married' || editFields.marital_status === 'Common-Law';

    return (
        <>
            <div className="client-search-container">
                <input
                    id="client-search-input"
                    type="text"
                    className="client-search-input"
                    placeholder="Search by name..."
                    value={searchName}
                    onChange={(e) => setSearchName(e.target.value)}
                />
            </div>

            <div className="cs-create-btn-container">
                <button className="cs-btn-add" onClick={openAddModal}>Add Client</button>
            </div>

            {updateMessage && <div className="client-error-msg">{updateMessage}</div>}

            {loading ? (
                <div className="loading-container"><div className="loading">Loading...</div></div>
            ) : filtered.length > 0 ? (
                <div className="client-list">
                    {filtered.map((client) => {
                        const isSelected = currentClientID === client.id;
                        const isExpanded = expandedCards.has(client.id);
                        const clientHasAdditional = hasAdditionalFields(client);
                        return (
                            <div key={client.id} className={`client-row${isSelected ? ' client-row--selected' : ''}`}>
                                <div className="client-main">
                                    <div className="client-meta">
                                        <span className="client-tag name-tag">{client.client_name}</span>
                                        {client.do_not_contact && (
                                            <span className="client-tag dnc-tag">Do Not Contact</span>
                                        )}


                                    </div>
                                    <div className="client-info-container">
                                        {client.emails && <div className="client-info">
                                            <div ><strong className="text-color--muted-orange">Email:</strong> </div>
                                            <div className="client-info-value">{client.emails.replace(',', ', ')}</div>
                                        </div>}
                                        {client.phone && <div className="client-info">
                                            <div><strong className="text-color--muted-orange">Phone:</strong> </div>
                                            <div className="client-info-value">{client.phone}</div>
                                        </div>}
                                        {client.my_cra && (
                                            <div className="client-info">
                                                <div><strong className="text-color--muted-orange">My CRA:</strong> </div>
                                                <div className="client-info-value">{client.my_cra ? 'Yes' : 'No'}</div>
                                            </div>
                                        )}
                                        {client.auth_rep && (
                                            <div className="client-info">
                                                <div><strong className="text-color--muted-orange">Auth Rep:</strong> </div>
                                                <div className="client-info-value">{client.auth_rep ? 'Yes' : 'No'}</div>
                                            </div>
                                        )}
                                        {client.notes && <div className="client-info">
                                            <div><strong className="text-color--muted-orange">Notes:</strong> </div>
                                            <div className="client-info-value">{client.notes}</div>
                                        </div>}
                                    </div>

                                    {/* Per-card additional fields panel */}
                                    {clientHasAdditional && isExpanded && (
                                        <div className="client-additional-panel">
                                            {ADDITIONAL_FIELD_KEYS.map((key) => {
                                                const display = renderAdditionalValue(client, key);
                                                if (!display) return null;
                                                return (
                                                    <div key={key} className="client-additional-row">
                                                        <span className="client-additional-label">
                                                            {ADDITIONAL_FIELD_LABELS[key]}
                                                        </span>
                                                        <span className="client-additional-value">{display}</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                                <div className="client-actions">
                                    <button
                                        className="modal-btn modal-btn-select"
                                        onClick={() => {
                                            if (isSelected) {
                                                setCurrentClientID(null);
                                                setCurrentClientName(null);
                                            } else {
                                                setCurrentClientID(client.id);
                                                setCurrentClientName(client.client_name);
                                            }
                                        }}
                                        title={isSelected ? 'Deselect client' : 'Select client'}
                                    >
                                        {isSelected ? 'Selected ✓' : 'Select'}
                                    </button>
                                    <button
                                        className="modal-btn modal-btn-cancel"
                                        onClick={() => openEdit(client)}
                                    >
                                        Edit
                                    </button>
                                    {clientHasAdditional && (
                                        <button
                                            className={`modal-btn modal-btn-additional${isExpanded ? ' modal-btn-additional--open' : ''}`}
                                            onClick={() => toggleExpandCard(client.id)}
                                            title={isExpanded ? 'Hide additional fields' : 'Show additional fields'}
                                        >
                                            {isExpanded ? 'Less ▴' : 'More ▾'}
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="loading-container">
                    <div className="loading">No clients found.</div>
                </div>
            )}

            {editingClient && (
                <div className="modal-overlay" onClick={closeEdit}>
                    <div className="modal client-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2 className="modal-title">Edit Client</h2>
                            <button className="modal-close" onClick={closeEdit}>&#x2715;</button>
                        </div>

                        <div className="modal-body">
                            <label className="modal-label">Full Name</label>
                            <input
                                className="cs-modal-input"
                                name="client_name"
                                value={editFields.client_name}
                                onChange={handleFieldChange}
                                placeholder="Full Name"
                            />

                            <label className="modal-label">Email(s)</label>
                            <input
                                className="cs-modal-input"
                                name="emails"
                                value={editFields.emails}
                                onChange={handleFieldChange}
                                placeholder="Email(s)"
                            />

                            <label className="modal-label">Phone</label>
                            <input
                                className="cs-modal-input"
                                name="phone"
                                value={editFields.phone}
                                onChange={handleFieldChange}
                                placeholder="Phone"
                            />

                            <div className="modal-row">
                                <div className="modal-col">
                                    <label className="modal-label">My CRA</label>
                                    <div className="modal-language-toggle">
                                        <button
                                            type="button"
                                            className={`modal-lang-btn${editFields.my_cra ? ' modal-lang-btn--active' : ''}`}
                                            onClick={() => handleFieldChange({ target: { name: 'my_cra', value: true } })}
                                        >
                                            Yes
                                        </button>
                                        <button
                                            type="button"
                                            className={`modal-lang-btn${!editFields.my_cra ? ' modal-lang-btn--active' : ''}`}
                                            onClick={() => handleFieldChange({ target: { name: 'my_cra', value: false } })}
                                        >
                                            No
                                        </button>
                                    </div>
                                </div>
                                <div className="modal-col">
                                    <label className="modal-label">Authorized Representative</label>
                                    <div className="modal-language-toggle">
                                        <button
                                            type="button"
                                            className={`modal-lang-btn${editFields.auth_rep ? ' modal-lang-btn--active' : ''}`}
                                            onClick={() => handleFieldChange({ target: { name: 'auth_rep', value: true } })}
                                        >
                                            Yes
                                        </button>
                                        <button
                                            type="button"
                                            className={`modal-lang-btn${!editFields.auth_rep ? ' modal-lang-btn--active' : ''}`}
                                            onClick={() => handleFieldChange({ target: { name: 'auth_rep', value: false } })}
                                        >
                                            No
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <label className="modal-label">Notes</label>
                            <textarea
                                className="cs-modal-textarea"
                                name="notes"
                                rows={4}
                                value={editFields.notes}
                                onChange={handleFieldChange}
                                placeholder="Notes..."
                            />

                            <div className="modal-checkbox-row">
                                <input
                                    type="checkbox"
                                    id="do_not_contact"
                                    name="do_not_contact"
                                    checked={editFields.do_not_contact}
                                    onChange={handleFieldChange}
                                />
                                <label htmlFor="do_not_contact" className="modal-checkbox-label">Do Not Contact</label>
                            </div>

                            {editFields.do_not_contact && (
                                <>
                                    <label className="modal-label">Reason</label>
                                    <input
                                        className="cs-modal-input"
                                        name="do_not_contact_reason"
                                        value={editFields.do_not_contact_reason}
                                        onChange={handleFieldChange}
                                        placeholder="Reason..."
                                    />
                                </>
                            )}

                            {/* ── Additional Fields ─────────────────────────────── */}
                            <div className="modal-section-divider">
                                <span className="modal-section-label">Additional Fields</span>
                            </div>

                            <div className="modal-row">
                                <div className="modal-col">
                                    <label className="modal-label">Title</label>
                                    <select
                                        className="modal-select"
                                        name="title"
                                        value={editFields.title}
                                        onChange={handleFieldChange}
                                    >
                                        {TITLE_OPTIONS.map(opt => (
                                            <option key={opt} value={opt}>{opt || '— Select —'}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="modal-col">
                                    <label className="modal-label">Language</label>
                                    <div className="modal-language-toggle">
                                        <button
                                            type="button"
                                            className={`modal-lang-btn${editFields.language === 'english' ? ' modal-lang-btn--active' : ''}`}
                                            onClick={() => setEditFields(p => ({ ...p, language: 'english' }))}
                                        >
                                            English
                                        </button>
                                        <button
                                            type="button"
                                            className={`modal-lang-btn${editFields.language === 'french' ? ' modal-lang-btn--active' : ''}`}
                                            onClick={() => setEditFields(p => ({ ...p, language: 'french' }))}
                                        >
                                            French
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <label className="modal-label">SIN</label>
                            <input
                                className="cs-modal-input"
                                name="sin"
                                value={editFields.sin}
                                onChange={handleFieldChange}
                                placeholder="Social Insurance Number"
                            />

                            <label className="modal-label">Date of Birth</label>
                            <input
                                className="cs-modal-input"
                                type="date"
                                name="date_of_birth"
                                value={editFields.date_of_birth}
                                onChange={handleFieldChange}
                            />

                            <label className="modal-label">Address</label>
                            <input
                                className="cs-modal-input"
                                name="address"
                                value={editFields.address}
                                onChange={handleFieldChange}
                                placeholder="Mailing Address"
                            />

                            <div className="modal-row">
                                <div className="modal-col">
                                    <label className="modal-label">Marital Status</label>
                                    <select
                                        className="modal-select"
                                        name="marital_status"
                                        value={editFields.marital_status}
                                        onChange={handleFieldChange}
                                    >
                                        {MARITAL_OPTIONS.map(opt => (
                                            <option key={opt} value={opt}>{opt || '— Select —'}</option>
                                        ))}
                                    </select>
                                </div>
                                {editShowSpouseName && (
                                    <div className="modal-col">
                                        <label className="modal-label">Spouse Name</label>
                                        <input
                                            className="cs-modal-input"
                                            name="spouse_name"
                                            value={editFields.spouse_name}
                                            onChange={handleFieldChange}
                                            placeholder="Spouse / Partner Name"
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="modal-checkbox-row">
                                <input
                                    type="checkbox"
                                    id="citizenship_status"
                                    name="citizenship_status"
                                    checked={editFields.citizenship_status}
                                    onChange={handleFieldChange}
                                />
                                <label htmlFor="citizenship_status" className="modal-checkbox-label">Canadian Citizen</label>
                            </div>

                            {saveError && <p className="modal-error">{saveError}</p>}
                        </div>

                        <div className="modal-footer">
                            <button
                                className={`modal-btn ${confirmDelete ? 'modal-btn-confirm-delete' : 'modal-btn-delete'}`}
                                onClick={deleteClient}
                                disabled={deleting || saving}
                            >
                                {deleting ? 'Deleting...' : confirmDelete ? 'Confirm Delete' : 'Delete'}
                            </button>
                            <div className="modal-footer-right">
                                <button className="modal-btn modal-btn-cancel" onClick={closeEdit} disabled={saving || deleting}>
                                    Cancel
                                </button>
                                <button className="modal-btn modal-btn-save" onClick={saveEdit} disabled={saving || deleting}>
                                    {saving ? 'Saving...' : 'Save'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {addModalOpen && (
                <div className="modal-overlay" onClick={closeAddModal}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2 className="modal-title">Add Client</h2>
                            <button className="modal-close" onClick={closeAddModal}>&#x2715;</button>
                        </div>
                        <div className="modal-body">
                            <CreateClient closeCreateModal={closeAddModal} setClients={setClients} />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default Search;
